import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';

const BulkActionToolbar = ({ 
  selectedCount, 
  onBulkAction, 
  onClearSelection,
  onExport 
}) => {
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkAction, setBulkAction] = useState('');
  const [bulkValue, setBulkValue] = useState('');

  const bulkActionOptions = [
    { value: '', label: 'Select action...' },
    { value: 'update-category', label: 'Update Category' },
    { value: 'adjust-price', label: 'Adjust Price' },
    { value: 'update-supplier', label: 'Update Supplier' },
    { value: 'mark-discontinued', label: 'Mark as Discontinued' },
    { value: 'delete', label: 'Delete Products' }
  ];

  const categoryOptions = [
    { value: 'Electronics', label: 'Electronics' },
    { value: 'Clothing', label: 'Clothing' },
    { value: 'Home & Garden', label: 'Home & Garden' },
    { value: 'Sports', label: 'Sports' },
    { value: 'Books', label: 'Books' },
    { value: 'Automotive', label: 'Automotive' }
  ];

  const supplierOptions = [
    { value: 'TechCorp', label: 'TechCorp' },
    { value: 'FashionHub', label: 'FashionHub' },
    { value: 'HomeGoods Inc', label: 'HomeGoods Inc' },
    { value: 'SportsPro', label: 'SportsPro' },
    { value: 'BookWorld', label: 'BookWorld' }
  ];

  const handleBulkAction = () => {
    if (bulkAction && (bulkValue || ['mark-discontinued', 'delete']?.includes(bulkAction))) {
      onBulkAction(bulkAction, bulkValue);
      setShowBulkModal(false);
      setBulkAction('');
      setBulkValue('');
    }
  };

  const renderBulkValueInput = () => {
    switch (bulkAction) {
      case 'update-category':
        return (
          <Select
            label="New Category"
            options={categoryOptions}
            value={bulkValue}
            onChange={setBulkValue}
            required
          />
        );
      case 'adjust-price':
        return (
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Price Adjustment</label>
            <div className="grid grid-cols-2 gap-2">
              <Select
                options={[
                  { value: 'increase', label: 'Increase by' },
                  { value: 'decrease', label: 'Decrease by' },
                  { value: 'set', label: 'Set to' }
                ]}
                value={bulkValue?.split('|')?.[0] || ''}
                onChange={(type) => setBulkValue(`${type}|${bulkValue?.split('|')?.[1] || ''}`)}
              />
              <Input
                type="number"
                placeholder="Amount"
                value={bulkValue?.split('|')?.[1] || ''}
                onChange={(e) => setBulkValue(`${bulkValue?.split('|')?.[0] || 'increase'}|${e?.target?.value}`)}
              />
            </div>
          </div>
        );
      case 'update-supplier':
        return (
          <Select
            label="New Supplier"
            options={supplierOptions}
            value={bulkValue}
            onChange={setBulkValue}
            required
          />
        );
      case 'mark-discontinued':
        return (
          <div className="p-4 bg-warning/10 border border-warning/20 rounded-lg">
            <p className="text-sm text-warning-foreground">
              This will mark {selectedCount} product{selectedCount !== 1 ? 's' : ''} as discontinued.
            </p>
          </div>
        );
      case 'delete':
        return (
          <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
            <p className="text-sm text-destructive-foreground">
              <strong>Warning:</strong> This will permanently delete {selectedCount} product{selectedCount !== 1 ? 's' : ''}. This action cannot be undone.
            </p>
          </div>
        );
      default:
        return null;
    }
  };

  if (selectedCount === 0) {
    return null;
  }

  return (
    <>
      <div className="bg-primary/10 border border-primary/20 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center justify-between sm:justify-start space-x-4">
            <div className="flex items-center space-x-2">
              <Icon name="CheckSquare" size={20} className="text-primary" />
              <span className="font-medium text-primary text-sm sm:text-base">
                {selectedCount} product{selectedCount !== 1 ? 's' : ''} selected
              </span>
            </div>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={onClearSelection}
              iconName="X"
              iconPosition="left"
              iconSize={14}
              className="text-primary hover:text-primary"
            >
              <span className="hidden sm:inline">Clear Selection</span>
              <span className="sm:hidden">Clear</span>
            </Button>
          </div>

          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowBulkModal(true)}
              iconName="Edit"
              iconPosition="left"
              iconSize={14}
              className="flex-1 sm:flex-none"
            >
              <span className="hidden sm:inline">Bulk Actions</span>
              <span className="sm:hidden">Bulk</span>
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={onExport}
              iconName="Download"
              iconPosition="left"
              iconSize={14}
              className="flex-1 sm:flex-none"
            >
              <span className="hidden sm:inline">Export Selected</span>
              <span className="sm:hidden">Export</span>
            </Button>
          </div>
        </div>
      </div>
      {/* Bulk Action Modal */}
      {showBulkModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-card border border-border rounded-lg shadow-modal w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base sm:text-lg font-semibold text-foreground">Bulk Actions</h3>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    setShowBulkModal(false);
                    setBulkAction('');
                    setBulkValue('');
                  }}
                >
                  <Icon name="X" size={20} />
                </Button>
              </div>

              <div className="space-y-4">
                <div>
                  <p className="text-sm text-muted-foreground mb-3">
                    Apply action to {selectedCount} selected product{selectedCount !== 1 ? 's' : ''}
                  </p>
                  
                  <Select
                    label="Action"
                    options={bulkActionOptions}
                    value={bulkAction}
                    onChange={setBulkAction}
                    required
                  />
                </div>

                {bulkAction && (
                  <div>
                    {renderBulkValueInput()}
                  </div>
                )}
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end space-y-2 sm:space-y-0 sm:space-x-3 mt-6 pt-4 border-t border-border">
                <Button
                  variant="ghost"
                  onClick={() => {
                    setShowBulkModal(false);
                    setBulkAction('');
                    setBulkValue('');
                  }}
                  className="w-full sm:w-auto"
                >
                  Cancel
                </Button>
                <Button
                  variant={bulkAction === 'delete' ? 'destructive' : 'default'}
                  onClick={handleBulkAction}
                  disabled={!bulkAction || (!bulkValue && !['mark-discontinued', 'delete']?.includes(bulkAction))}
                  iconName={bulkAction === 'delete' ? 'Trash2' : 'Check'}
                  iconPosition="left"
                  iconSize={16}
                  className="w-full sm:w-auto"
                >
                  {bulkAction === 'delete' ? 'Delete Products' : 'Apply Action'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BulkActionToolbar;